/**
 * Ad Manager Service - Full Prebid.js Header Bidding + GAM + Revive Integration
 * 
 * This is the central ad orchestration service that:
 * 1. Loads Prebid.js and Google Publisher Tag (GPT) scripts
 * 2. Configures Prebid bidder adapters (AppNexus, Rubicon, OpenX, Amazon TAM, Revive)
 * 3. Runs header bidding auctions via pbjs.requestBids()
 * 4. Sends winning bids to GAM via setTargetingForGPTAsync()
 * 5. Falls back to Revive ad server when GAM/Prebid unavailable
 * 6. Tracks impressions, clicks, and revenue through AdAnalytics
 */

import PrivacyConsentService from './PrivacyConsentService';
import { reviveAdService } from './ReviveAdService';
import { adSettingsService, AdSettings } from './AdSettingsService';
import { AD_CONFIG, BIDDER_CONFIGS, AD_SLOT_CONFIGS, getAdSlotConfig } from '../config/adConfig';
import { loadPrebidScript, loadGoogleAdManagerScript, isPrebidReady, isGoogleAdManagerReady } from '../utils/scriptLoader';
import { AdSlotConfig, AdError, AdErrorType, AdMetrics, BidderConfig, TargetingData } from '../types/ads';

interface RegisteredSlot {
  slotId: string;
  sizes: Array<[number, number]>;
  targeting: Record<string, any>;
  refreshRate: number;
  lazy: boolean;
  priority: number;
  gptSlot?: any; // googletag.Slot reference
  lastRefresh?: number;
  refreshTimer?: NodeJS.Timeout;
}

interface DirectDeal {
  dealId: string;
  advertiserId: string;
  campaignId: string;
  floorPrice: number;
  priority: number;
  startDate: Date;
  endDate: Date;
  budget: number;
  spent: number;
  impressionsServed: number;
  impressionCap?: number;
  targeting: Record<string, any>;
  status: 'active' | 'paused' | 'expired';
}

interface PMPDeal {
  dealId: string;
  buyerId: string;
  sellerId: string;
  floorPrice: number;
  priority: number;
  dealType: 'preferred' | 'guaranteed';
  budget: number;
  spent: number;
  targeting: Record<string, any>;
  status: 'active' | 'paused' | 'expired';
}

interface FloorPriceConfig {
  [slotId: string]: number;
}

class AdManager {
  private static instance: AdManager;
  private _isInitialized: boolean = false;
  private initializationPromise: Promise<void> | null = null;
  private privacyService: typeof PrivacyConsentService;
  private registeredSlots: Map<string, RegisteredSlot> = new Map();
  private directDeals: DirectDeal[] = [];
  private pmpDeals: PMPDeal[] = [];
  private floorPrices: FloorPriceConfig = {};
  private errorHandlers: Array<(error: AdError) => void> = [];
  private prebidLoaded: boolean = false;
  private gamLoaded: boolean = false;
  private reviveEnabled: boolean = false;
  private settings: AdSettings | null = null;
  private metricsCache: AdMetrics = {
    rpm: 0,
    fillRate: 0,
    ctr: 0,
    viewability: 0,
    loadTime: 0,
    revenue: 0
  };
  private impressionCount: number = 0;
  private clickCount: number = 0;
  private dynamicRefreshSlots: Set<string> = new Set();

  private constructor() {
    this.privacyService = PrivacyConsentService;
    console.log('📱 AdManager: Creating instance...');
  }

  public static getInstance(): AdManager {
    if (!AdManager.instance) {
      AdManager.instance = new AdManager();
    }
    return AdManager.instance;
  }

  /**
   * Check if ads are initialized
   */
  public get isInitialized(): boolean {
    return this._isInitialized;
  }

  /**
   * Initialize the full ad stack: Prebid.js + GAM + Revive
   */
  public async initializeAds(): Promise<void> {
    if (this._isInitialized) {
      console.log('📱 AdManager: Already initialized');
      return;
    }

    // Prevent concurrent initialization
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this._doInitialize();
    return this.initializationPromise;
  }

  private async _doInitialize(): Promise<void> {
    try {
      console.log('📱 AdManager: Starting full initialization...');

      // Check privacy consent
      if (this.privacyService.requiresConsent() && !this.privacyService.hasConsent()) {
        console.log('📱 AdManager: Waiting for privacy consent...');
        window.addEventListener('privacy:consent-updated', () => {
          this.handleConsentUpdate();
        });
        return;
      }

      if (!this.privacyService.isMarketingAllowed()) {
        console.log('📱 AdManager: Marketing consent not given, ads disabled');
        return;
      }

      // Load ad settings from backend
      await this.loadAdSettings();

      // Load external scripts in parallel
      await this.loadExternalScripts();

      // Configure Prebid.js if loaded
      if (this.prebidLoaded) {
        this.configurePrebid();
      }

      // Configure GAM if loaded
      if (this.gamLoaded) {
        this.configureGAM();
      }

      // Initialize Revive if enabled
      await this.initializeRevive();

      this._isInitialized = true;
      console.log('✅ AdManager: Fully initialized', {
        prebid: this.prebidLoaded,
        gam: this.gamLoaded,
        revive: this.reviveEnabled,
        slotsRegistered: this.registeredSlots.size
      });

    } catch (error) {
      console.error('❌ AdManager: Initialization failed:', error);
      // Still mark as initialized so we can fall back to Revive
      this._isInitialized = true;
    } finally {
      this.initializationPromise = null;
    }
  }

  /**
   * Load ad settings from the backend settings service
   */
  private async loadAdSettings(): Promise<void> {
    try {
      this.settings = await adSettingsService.getSettings();
      this.reviveEnabled = this.settings.reviveEnabled;
      console.log('📱 AdManager: Settings loaded', {
        prebidEnabled: this.settings.prebidEnabled,
        reviveEnabled: this.settings.reviveEnabled,
        reviveServerUrl: this.settings.reviveServerUrl,
        amazonTAMEnabled: this.settings.amazonTAMEnabled,
        debugMode: this.settings.debugMode
      });
    } catch (error) {
      console.warn('📱 AdManager: Failed to load settings, using defaults:', error);
      this.settings = null;
      // Check env var fallback for Revive
      this.reviveEnabled = process.env.REACT_APP_REVIVE_ENABLED === 'true';
    }
  }

  /**
   * Load Prebid.js and GPT scripts
   */
  private async loadExternalScripts(): Promise<void> {
    const scriptPromises: Promise<void>[] = [];

    // Load Prebid.js if enabled
    const prebidEnabled = this.settings?.prebidEnabled !== false;
    if (prebidEnabled) {
      scriptPromises.push(
        loadPrebidScript()
          .then(() => {
            this.prebidLoaded = true;
            console.log('📱 AdManager: Prebid.js loaded');
          })
          .catch((error) => {
            console.warn('📱 AdManager: Prebid.js failed to load, will use fallback:', error);
            this.prebidLoaded = false;
          })
      );
    }

    // Load Google Publisher Tag
    scriptPromises.push(
      loadGoogleAdManagerScript()
        .then(() => {
          this.gamLoaded = true;
          console.log('📱 AdManager: GPT loaded');
        })
        .catch((error) => {
          console.warn('📱 AdManager: GPT failed to load, will use fallback:', error);
          this.gamLoaded = false;
        })
    );

    // Load Amazon TAM if enabled
    if (this.settings?.amazonTAMEnabled) {
      scriptPromises.push(this.loadAmazonTAM());
    }

    // Wait for all scripts with a timeout
    await Promise.race([
      Promise.allSettled(scriptPromises),
      new Promise<void>((resolve) => setTimeout(resolve, AD_CONFIG.prebid.timeout + 2000))
    ]);
  }

  /**
   * Load Amazon TAM (Transparent Ad Marketplace) script
   */
  private async loadAmazonTAM(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (window.apstag) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://c.amazon-adsystem.com/aax2/apstag.js';
      script.async = true;
      script.onload = () => {
        if (window.apstag) {
          window.apstag.init({
            pubID: BIDDER_CONFIGS.amazon?.params?.pubID || 'fashion-rating-app',
            adServer: 'googletag',
            bidTimeout: 2000
          });
          console.log('📱 AdManager: Amazon TAM initialized');
        }
        resolve();
      };
      script.onerror = () => {
        console.warn('📱 AdManager: Amazon TAM failed to load');
        resolve(); // Don't reject, just continue without TAM
      };
      document.head.appendChild(script);
    });
  }

  /**
   * Configure Prebid.js with bidder adapters and settings
   */
  private configurePrebid(): void {
    if (!window.pbjs) {
      console.warn('📱 AdManager: pbjs not available for configuration');
      return;
    }

    window.pbjs.que.push(() => {
      // Set Prebid configuration
      window.pbjs!.setConfig({
        debug: this.settings?.debugMode || process.env.NODE_ENV === 'development',
        bidderTimeout: AD_CONFIG.prebid.timeout,
        enableSendAllBids: AD_CONFIG.prebid.enableSendAllBids,
        useBidCache: AD_CONFIG.prebid.useBidCache,
        priceGranularity: AD_CONFIG.prebid.priceGranularity,
        currency: {
          adServerCurrency: 'USD'
        },
        userSync: {
          filterSettings: {
            iframe: { bidders: '*', filter: 'include' },
            image: { bidders: '*', filter: 'include' }
          },
          syncsPerBidder: 5,
          syncDelay: 3000,
          auctionDelay: 0
        },
        cache: {
          url: 'https://prebid.adnxs.com/pbc/v1/cache'
        },
        // Price floors module — sets minimum CPM per media type
        floors: {
          enforcement: {
            floorDeals: true,
            bidAdjustment: true
          },
          data: {
            currency: 'USD',
            schema: { fields: ['mediaType'] },
            values: {
              'banner': 0.10,  // $0.10 floor for banner ads
              'native': 0.15,  // $0.15 floor for native ads
              'video': 0.50    // $0.50 floor for video ads
            }
          }
        },
        // Consent management for GDPR (TCF 2.0) and US Privacy (CCPA)
        consentManagement: {
          gdpr: {
            cmpApi: 'iab',
            timeout: 8000,
            defaultGdprScope: true
          },
          usp: {
            cmpApi: 'iab',
            timeout: 1000
          }
        },
        // Bidder-specific overrides
        bidderSequence: 'random',  // Randomize bidder order for fairness
        targetingControls: {
          allowTargetingKeys: ['BIDDER', 'AD_ID', 'PRICE_BUCKET', 'SIZE', 'DEAL', 'SOURCE', 'FORMAT', 'UUID'],
          alwaysIncludeDeals: true
        }
      });

      // Set up Prebid analytics if enabled
      if (this.settings?.debugMode || process.env.NODE_ENV === 'development') {
        try {
          window.pbjs!.enableAnalytics([{
            provider: 'ga',
            options: {
              global: 'ga',
              enableDistribution: false
            }
          }]);
        } catch (analyticsError) {
          console.warn('📱 AdManager: Prebid analytics setup failed:', analyticsError);
        }
      }

      // Listen for bid events for tracking
      window.pbjs!.onEvent('bidWon', (data: any) => {
        console.log('📱 AdManager: Bid won', {
          bidder: data.bidder,
          cpm: data.cpm,
          adUnitCode: data.adUnitCode
        });
        this.metricsCache.revenue += (data.cpm || 0) / 1000;
      });

      window.pbjs!.onEvent('auctionEnd', (data: any) => {
        console.log('📱 AdManager: Auction ended', {
          auctionId: data.auctionId,
          bidsReceived: data.bidsReceived?.length || 0,
          noBids: data.noBids?.length || 0
        });
      });

      window.pbjs!.onEvent('bidTimeout', (data: any) => {
        console.warn('📱 AdManager: Bid timeout', data);
      });

      console.log('📱 AdManager: Prebid.js configured');
    });
  }

  /**
   * Configure Google Ad Manager (GPT)
   */
  private configureGAM(): void {
    if (!window.googletag) {
      console.warn('📱 AdManager: googletag not available for configuration');
      return;
    }

    window.googletag.cmd.push(() => {
      const gamConfig = AD_CONFIG.gam;

      // Enable Single Request Architecture for better performance
      if (gamConfig.enableSRA) {
        window.googletag!.pubads().enableSingleRequest();
      }

      // Collapse empty divs to avoid blank spaces
      if (gamConfig.collapseEmptyDivs) {
        window.googletag!.pubads().collapseEmptyDivs();
      }

      // Enable lazy loading
      if (gamConfig.enableLazyLoad && gamConfig.lazyLoadConfig) {
        window.googletag!.pubads().enableLazyLoad({
          fetchMarginPercent: gamConfig.lazyLoadConfig.fetchMarginPercent,
          renderMarginPercent: gamConfig.lazyLoadConfig.renderMarginPercent,
          mobileScaling: gamConfig.lazyLoadConfig.mobileScaling
        });
      }

      // Set privacy settings based on consent
      const personalizedAds = this.privacyService.canShowPersonalizedAds();
      window.googletag!.pubads().setPrivacySettings({
        restrictDataProcessing: !personalizedAds,
        childDirectedTreatment: false
      });

      // Listen for slot render events
      window.googletag!.pubads().addEventListener('slotRenderEnded', (event: any) => {
        const slotId = event.slot.getSlotElementId();
        const isEmpty = event.isEmpty;

        if (isEmpty) {
          console.log(`📱 AdManager: GAM slot ${slotId} rendered empty, trying Revive fallback`);
          this.tryReviveFallback(slotId);
        } else {
          console.log(`📱 AdManager: GAM slot ${slotId} rendered successfully`, {
            advertiserId: event.advertiserId,
            campaignId: event.campaignId,
            lineItemId: event.lineItemId,
            size: event.size
          });
          this.impressionCount++;
          this.updateMetrics();
        }
      });

      window.googletag!.pubads().addEventListener('impressionViewable', (event: any) => {
        const slotId = event.slot.getSlotElementId();
        console.log(`📱 AdManager: Viewable impression for ${slotId}`);
      });

      // Enable services
      window.googletag!.enableServices();

      console.log('📱 AdManager: GAM configured with network:', gamConfig.networkId);
    });
  }

  /**
   * Initialize Revive Ad Server integration
   */
  private async initializeRevive(): Promise<void> {
    if (!this.reviveEnabled) {
      console.log('📱 AdManager: Revive not enabled');
      return;
    }

    try {
      const reviveConfig = this.settings
        ? await adSettingsService.getReviveConfig()
        : {
            enabled: true,
            serverUrl: process.env.REACT_APP_CUSTOM_AD_SERVER_URL || process.env.REACT_APP_REVIVE_SERVER_URL || 'http://localhost:3000',
            zones: {
              header: parseInt(process.env.REACT_APP_REVIVE_ZONE_HEADER || '1'),
              sidebar: parseInt(process.env.REACT_APP_REVIVE_ZONE_SIDEBAR || '2'),
              mobile: parseInt(process.env.REACT_APP_REVIVE_ZONE_MOBILE || '3'),
              inContent: parseInt(process.env.REACT_APP_REVIVE_ZONE_INCONTENT || '4'),
              footer: parseInt(process.env.REACT_APP_REVIVE_ZONE_FOOTER || '5')
            },
            fallbackAds: { enabled: true, defaultBanners: [] }
          };

      await reviveAdService.initialize(reviveConfig);
      console.log('📱 AdManager: Ad server initialized at', reviveConfig.serverUrl);
    } catch (error) {
      console.warn('📱 AdManager: Ad server initialization failed:', error);
      this.reviveEnabled = false;
    }
  }

  /**
   * Handle privacy consent updates
   */
  private handleConsentUpdate(): void {
    const consentSettings = this.privacyService.getConsentSettings();
    if (consentSettings?.marketing) {
      console.log('📱 AdManager: Marketing consent given, initializing ads...');
      this.initializeAds();
    } else {
      console.log('📱 AdManager: Marketing consent not given, ads disabled');
    }
  }

  /**
   * Register an ad slot for management
   */
  public registerAdSlot(config: AdSlotConfig & { refreshRate?: number; lazy?: boolean; priority?: number }): void {
    const slot: RegisteredSlot = {
      slotId: config.slotId,
      sizes: config.sizes,
      targeting: config.targeting || {},
      refreshRate: config.refreshRate || 0,
      lazy: config.lazy ?? true,
      priority: config.priority || 1
    };

    this.registeredSlots.set(config.slotId, slot);
    console.log(`📱 AdManager: Registered slot ${config.slotId}`, {
      sizes: config.sizes,
      priority: slot.priority
    });
  }

  /**
   * Load and display an ad in a specific slot using the full Prebid → GAM → Revive waterfall
   */
  public async loadAdSlot(slotId: string, targeting?: Record<string, any>): Promise<void> {
    if (!this._isInitialized) {
      await this.initializeAds();
    }

    if (!this.privacyService.isMarketingAllowed()) {
      console.log(`📱 AdManager: Ads disabled by privacy settings for ${slotId}`);
      return;
    }

    const slot = this.registeredSlots.get(slotId);
    if (!slot) {
      console.warn(`📱 AdManager: Slot ${slotId} not registered`);
      return;
    }

    const mergedTargeting = { ...slot.targeting, ...targeting };
    const startTime = Date.now();

    console.log(`📱 AdManager: Loading ad for slot ${slotId}`, { targeting: mergedTargeting });

    try {
      // Strategy: Try custom ad server first (guaranteed fill for configured zones),
      // then fall back to GAM/Prebid (for AdSense/SSP demand when available).
      // This ensures ads always fill from whatever source has inventory.
      // When AdSense or other SSPs start returning ads, they'll compete via Prebid/GAM.
      
      let filled = false;

      // 1. Try custom ad server first — it has real ads configured
      if (this.reviveEnabled && reviveAdService.isEnabled()) {
        filled = await this.loadWithRevive(slotId, slot);
      }

      // 2. If custom ad server didn't fill, try Prebid + GAM (AdSense/SSP demand)
      if (!filled && this.prebidLoaded && this.gamLoaded) {
        await this.loadWithPrebidAndGAM(slotId, slot, mergedTargeting);
      } else if (!filled && this.gamLoaded) {
        await this.loadWithGAMOnly(slotId, slot, mergedTargeting);
      }

      // 3. If nothing filled, emit no-fill
      if (!filled && !this.prebidLoaded && !this.gamLoaded) {
        console.warn(`📱 AdManager: No ad source available for ${slotId}`);
        this.emitError({
          type: AdErrorType.NO_FILL,
          slotId,
          message: 'No ad source filled this slot',
          timestamp: new Date()
        });
      }

      const loadTime = Date.now() - startTime;
      slot.lastRefresh = Date.now();
      console.log(`📱 AdManager: Slot ${slotId} load completed in ${loadTime}ms`);

    } catch (error) {
      console.error(`📱 AdManager: Error loading slot ${slotId}:`, error);
      // Try Revive as last resort
      if (this.reviveEnabled) {
        await this.loadWithRevive(slotId, slot);
      }
    }
  }

  /**
   * Full Prebid.js header bidding auction → GAM ad serving
   */
  private async loadWithPrebidAndGAM(
    slotId: string,
    slot: RegisteredSlot,
    targeting: Record<string, any>
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!window.pbjs || !window.googletag) {
        reject(new Error('Prebid or GAM not available'));
        return;
      }

      // Build Prebid ad unit with all configured bidders
      const adUnit = this.buildPrebidAdUnit(slotId, slot);

      window.pbjs!.que.push(() => {
        // Remove any existing ad unit for this slot
        try {
          window.pbjs!.removeAdUnit(slotId);
        } catch (e) {
          // Ignore if not found
        }

        // Add the ad unit
        window.pbjs!.addAdUnits([adUnit]);

        // Fetch Amazon TAM bids in parallel if apstag is available
        const amazonBidPromise = this.fetchAmazonBids(slotId, slot);

        // Request bids from all configured Prebid bidders
        window.pbjs!.requestBids({
          adUnitCodes: [slotId],
          timeout: AD_CONFIG.prebid.timeout,
          bidsBackHandler: () => {
            console.log(`📱 AdManager: Prebid bids received for ${slotId}`);

            // Wait for Amazon bids to complete (with a short timeout)
            amazonBidPromise.then(() => {
              // Get highest CPM bids for logging
              const highestBids = window.pbjs!.getHighestCpmBids(slotId);
              if (highestBids.length > 0) {
                console.log(`📱 AdManager: Winning bid for ${slotId}:`, {
                  bidder: highestBids[0].bidder,
                  cpm: highestBids[0].cpm,
                  currency: highestBids[0].currency
                });
              } else {
                console.log(`📱 AdManager: No bids received for ${slotId}, GAM will fill`);
              }

              // Set targeting on GPT slots and display
              window.googletag!.cmd.push(() => {
                // Set Prebid targeting on GPT (includes all bidder key-values)
                window.pbjs!.setTargetingForGPTAsync([slotId]);

                // Define the GPT slot if not already defined
                if (!slot.gptSlot) {
                  const gamNetworkId = AD_CONFIG.gam.networkId;
                  const adUnitPath = `/${gamNetworkId}/fashion-rating-app/${slotId}`;

                  slot.gptSlot = window.googletag!.defineSlot(
                    adUnitPath,
                    slot.sizes,
                    slotId
                  );

                  if (slot.gptSlot) {
                    slot.gptSlot.addService(window.googletag!.pubads());

                    // Apply custom targeting
                    Object.entries(targeting).forEach(([key, value]) => {
                      if (typeof value === 'string' || Array.isArray(value)) {
                        slot.gptSlot.setTargeting(key, value);
                      }
                    });

                    // Apply floor price if set
                    const floorPrice = this.floorPrices[slotId];
                    if (floorPrice) {
                      slot.gptSlot.setTargeting('hb_pb_floor', String(floorPrice));
                    }
                  }
                }

                // Display the ad — GAM runs its own auction (AdX/AdSense +
                // Open Bidding partners) against the Prebid + Amazon bids
                window.googletag!.display(slotId);
                resolve();
              });
            });
          }
        });
      });
    });
  }

  /**
   * Fetch Amazon TAM bids via apstag.js and set targeting on GPT.
   * Returns a promise that resolves when Amazon bids are ready (or times out).
   */
  private fetchAmazonBids(slotId: string, slot: RegisteredSlot): Promise<void> {
    return new Promise<void>((resolve) => {
      if (!window.apstag) {
        resolve();
        return;
      }

      const amazonSlots = [{
        slotID: slotId,
        slotName: `fashion-rating-app/${slotId}`,
        sizes: slot.sizes.filter(s => s[0] > 1 && s[1] > 1) // exclude fluid
      }];

      try {
        window.apstag.fetchBids(
          { slots: amazonSlots, timeout: 2000 },
          (bids: any[]) => {
            if (window.googletag && bids && bids.length > 0) {
              window.googletag.cmd.push(() => {
                window.apstag!.setDisplayBids();
                console.log(`📱 AdManager: Amazon TAM bids set for ${slotId}`, bids);
              });
            }
            resolve();
          }
        );
      } catch (e) {
        console.warn('📱 AdManager: Amazon TAM fetchBids failed:', e);
        resolve();
      }
    });
  }

  /**
   * Build a Prebid.js ad unit configuration with all bidders including Revive
   */
  private buildPrebidAdUnit(slotId: string, slot: RegisteredSlot): any {
    const bids: any[] = [];

    // Add standard SSP bidders from config
    const bidderConfigs = Object.values(BIDDER_CONFIGS);
    for (const bidder of bidderConfigs) {
      // Skip Amazon — it goes through apstag.js, not Prebid adapter
      if (bidder.bidder === 'amazon') {
        continue;
      }

      // Index Exchange requires size in params — set per-slot
      if (bidder.bidder === 'ix') {
        // Add one bid entry per size for IX
        for (const size of slot.sizes) {
          if (size[0] > 1 && size[1] > 1) { // skip fluid [1,1]
            bids.push({
              bidder: 'ix',
              params: {
                ...bidder.params,
                size
              }
            });
          }
        }
        continue;
      }

      bids.push({
        bidder: bidder.bidder,
        params: { ...bidder.params }
      });
    }

    // Add custom ad server as a bidder if enabled
    if (this.reviveEnabled && reviveAdService.isEnabled()) {
      const zoneId = this.getZoneForSlot(slotId);
      if (zoneId > 0) {
        bids.push({
          bidder: 'customadserver',
          params: {
            zoneId: zoneId,
            floorPrice: this.floorPrices[slotId] || 0
          }
        });
      }
    }

    // Determine media types based on slot type
    const isNativeSlot = slotId.includes('native') || slotId.includes('in-feed');
    const mediaTypes: any = {
      banner: {
        sizes: slot.sizes
      }
    };

    // Add native media type for in-feed ad slots
    if (isNativeSlot) {
      mediaTypes.native = {
        title: { required: true, len: 80 },
        body: { required: true, len: 200 },
        image: { required: true, sizes: [300, 250] },
        sponsoredBy: { required: true, len: 50 },
        icon: { required: false, sizes: [50, 50] },
        clickUrl: { required: true },
        cta: { required: false, len: 30 }
      };
    }

    return {
      code: slotId,
      mediaTypes,
      bids
    };
  }

  /**
   * Map a slot ID to the corresponding zone ID
   */
  private getZoneForSlot(slotId: string): number {
    const zones = this.settings?.reviveZones || {
      header: parseInt(process.env.REACT_APP_REVIVE_ZONE_HEADER || '1'),
      sidebar: parseInt(process.env.REACT_APP_REVIVE_ZONE_SIDEBAR || '2'),
      mobile: parseInt(process.env.REACT_APP_REVIVE_ZONE_MOBILE || '3'),
      inContent: parseInt(process.env.REACT_APP_REVIVE_ZONE_INCONTENT || '4'),
      footer: parseInt(process.env.REACT_APP_REVIVE_ZONE_FOOTER || '5')
    };

    // Map slot IDs to Revive zones
    if (slotId.includes('hero') || slotId.includes('banner-top') || slotId.includes('search-results-top')) {
      return zones.header;
    }
    if (slotId.includes('sidebar') || slotId.includes('profile')) {
      return zones.sidebar;
    }
    if (slotId.includes('mobile')) {
      return zones.mobile;
    }
    if (slotId.includes('native') || slotId.includes('in-feed') || slotId.includes('travel')) {
      return zones.inContent;
    }
    if (slotId.includes('footer')) {
      return zones.footer;
    }

    // Default to sidebar zone
    return zones.sidebar;
  }

  /**
   * Load ad using GAM only (no Prebid header bidding)
   */
  private async loadWithGAMOnly(
    slotId: string,
    slot: RegisteredSlot,
    targeting: Record<string, any>
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      if (!window.googletag) {
        this.tryReviveFallback(slotId);
        resolve();
        return;
      }

      window.googletag.cmd.push(() => {
        if (!slot.gptSlot) {
          const gamNetworkId = AD_CONFIG.gam.networkId;
          const adUnitPath = `/${gamNetworkId}/fashion-rating-app/${slotId}`;

          slot.gptSlot = window.googletag!.defineSlot(
            adUnitPath,
            slot.sizes,
            slotId
          );

          if (slot.gptSlot) {
            slot.gptSlot.addService(window.googletag!.pubads());

            Object.entries(targeting).forEach(([key, value]) => {
              if (typeof value === 'string' || Array.isArray(value)) {
                slot.gptSlot.setTargeting(key, value);
              }
            });
          }
        }

        window.googletag!.display(slotId);
        resolve();
      });
    });
  }

  /**
   * Load ad directly from custom ad server (fallback when Prebid/GAM unavailable)
   */
  private async loadWithRevive(slotId: string, slot: RegisteredSlot): Promise<boolean> {
    if (!this.reviveEnabled || !reviveAdService.isEnabled()) {
      console.warn(`📱 AdManager: Custom ad server not available for fallback on ${slotId}`);
      return false;
    }

    const zoneId = this.getZoneForSlot(slotId);
    const width = slot.sizes[0]?.[0] || 300;
    const height = slot.sizes[0]?.[1] || 250;

    console.log(`📱 AdManager: Loading ad from custom ad server for ${slotId} (zone ${zoneId})`);

    const success = await reviveAdService.loadAdScript({
      zoneId,
      width,
      height,
      containerId: slotId
    });

    if (success) {
      this.impressionCount++;
      this.updateMetrics();
      console.log(`📱 AdManager: Custom ad server ad loaded for ${slotId}`);
      return true;
    } else {
      console.warn(`📱 AdManager: Custom ad server ad failed for ${slotId}`);
      return false;
    }
  }

  /**
   * Try custom ad server as fallback when GAM returns empty
   */
  private async tryReviveFallback(slotId: string): Promise<void> {
    if (!this.reviveEnabled || !reviveAdService.isEnabled()) {
      return;
    }

    const slot = this.registeredSlots.get(slotId);
    if (!slot) return;

    console.log(`📱 AdManager: Trying Revive fallback for ${slotId}`);
    await this.loadWithRevive(slotId, slot);
  }

  /**
   * Refresh a specific ad slot
   */
  public async refreshAd(slotId: string, targeting?: Record<string, any>): Promise<void> {
    if (!this._isInitialized || !this.privacyService.isMarketingAllowed()) {
      return;
    }

    const slot = this.registeredSlots.get(slotId);
    if (!slot) {
      console.warn(`📱 AdManager: Cannot refresh unregistered slot ${slotId}`);
      return;
    }

    console.log(`📱 AdManager: Refreshing slot ${slotId}`);

    // If we have a GPT slot, refresh through Prebid → GAM
    if (slot.gptSlot && this.prebidLoaded && window.pbjs && window.googletag) {
      const mergedTargeting = { ...slot.targeting, ...targeting };
      const adUnit = this.buildPrebidAdUnit(slotId, slot);

      window.pbjs.que.push(() => {
        try { window.pbjs!.removeAdUnit(slotId); } catch (e) { /* ignore */ }
        window.pbjs!.addAdUnits([adUnit]);

        window.pbjs!.requestBids({
          adUnitCodes: [slotId],
          timeout: AD_CONFIG.prebid.timeout,
          bidsBackHandler: () => {
            window.googletag!.cmd.push(() => {
              window.pbjs!.setTargetingForGPTAsync([slotId]);
              window.googletag!.pubads().refresh([slot.gptSlot]);
            });
          }
        });
      });
    } else if (slot.gptSlot && window.googletag) {
      // Refresh GAM only
      window.googletag.cmd.push(() => {
        window.googletag!.pubads().refresh([slot.gptSlot]);
      });
    } else if (this.reviveEnabled) {
      // Refresh via Revive
      await this.loadWithRevive(slotId, slot);
    }

    slot.lastRefresh = Date.now();
  }

  /**
   * Destroy a specific ad slot
   */
  public async destroyAd(slotId: string): Promise<void> {
    const slot = this.registeredSlots.get(slotId);
    if (!slot) return;

    // Clear refresh timer
    if (slot.refreshTimer) {
      clearInterval(slot.refreshTimer);
      slot.refreshTimer = undefined;
    }

    // Destroy GPT slot
    if (slot.gptSlot && window.googletag) {
      window.googletag.cmd.push(() => {
        window.googletag!.destroySlots([slot.gptSlot]);
      });
      slot.gptSlot = undefined;
    }

    // Remove Prebid ad unit
    if (window.pbjs) {
      window.pbjs.que.push(() => {
        try { window.pbjs!.removeAdUnit(slotId); } catch (e) { /* ignore */ }
      });
    }

    // Clear the DOM element
    const element = document.getElementById(slotId);
    if (element) {
      element.innerHTML = '';
    }

    this.registeredSlots.delete(slotId);
    this.dynamicRefreshSlots.delete(slotId);
    console.log(`📱 AdManager: Destroyed slot ${slotId}`);
  }

  /**
   * Display ad in a container (simple display call)
   */
  public displayAd(slotId: string): void {
    if (!this._isInitialized || !this.privacyService.isMarketingAllowed()) {
      return;
    }

    if (window.googletag) {
      window.googletag.cmd.push(() => {
        window.googletag!.display(slotId);
      });
    }
  }

  /**
   * Show banner ad in a container
   */
  public showBanner(containerId: string): void {
    if (!this._isInitialized || !this.privacyService.isMarketingAllowed()) {
      console.log('📱 AdManager: Ads disabled, not showing banner');
      return;
    }

    this.loadAdSlot(containerId);
  }

  /**
   * Show interstitial ad
   */
  public showInterstitial(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this._isInitialized || !this.privacyService.isMarketingAllowed()) {
        resolve(false);
        return;
      }

      // Interstitials are handled through GAM's out-of-page slots
      if (window.googletag) {
        window.googletag.cmd.push(() => {
          const interstitialSlot = window.googletag!.defineSlot(
            `/${AD_CONFIG.gam.networkId}/fashion-rating-app/interstitial`,
            [[1, 1]],
            'interstitial-container'
          );
          if (interstitialSlot) {
            interstitialSlot.addService(window.googletag!.pubads());
            window.googletag!.display('interstitial-container');
          }
          resolve(true);
        });
      } else {
        resolve(false);
      }
    });
  }

  /**
   * Show rewarded ad
   */
  public showRewarded(): Promise<{ success: boolean; reward?: any }> {
    return new Promise((resolve) => {
      if (!this._isInitialized || !this.privacyService.isMarketingAllowed()) {
        resolve({ success: false });
        return;
      }

      // Rewarded ads through GAM
      if (window.googletag) {
        window.googletag.cmd.push(() => {
          const rewardedSlot = window.googletag!.defineSlot(
            `/${AD_CONFIG.gam.networkId}/fashion-rating-app/rewarded`,
            [[1, 1]],
            'rewarded-container'
          );
          if (rewardedSlot) {
            rewardedSlot.addService(window.googletag!.pubads());
            window.googletag!.display('rewarded-container');
          }
          resolve({ success: true, reward: { type: 'coins', amount: 10 } });
        });
      } else {
        resolve({ success: false });
      }
    });
  }

  /**
   * Check if ads are enabled
   */
  public isEnabled(): boolean {
    return this._isInitialized && this.privacyService.isMarketingAllowed();
  }

  /**
   * Set error handler
   */
  public onError(handler: (error: AdError) => void): void {
    this.errorHandlers.push(handler);
  }

  /**
   * Emit error to all registered handlers
   */
  private emitError(error: AdError): void {
    this.errorHandlers.forEach(handler => {
      try {
        handler(error);
      } catch (e) {
        console.error('📱 AdManager: Error handler threw:', e);
      }
    });
  }

  /**
   * Enable dynamic refresh for a slot based on user engagement
   */
  public enableDynamicRefresh(slotId?: string): void {
    if (slotId) {
      this.dynamicRefreshSlots.add(slotId);
      const slot = this.registeredSlots.get(slotId);
      if (slot && slot.refreshRate > 0 && !slot.refreshTimer) {
        slot.refreshTimer = setInterval(() => {
          if (this.dynamicRefreshSlots.has(slotId)) {
            this.refreshAd(slotId);
          }
        }, slot.refreshRate * 1000);
      }
      console.log(`📱 AdManager: Dynamic refresh enabled for ${slotId}`);
    } else {
      // Enable for all registered slots
      this.registeredSlots.forEach((slot, id) => {
        this.enableDynamicRefresh(id);
      });
    }
  }

  /**
   * Disable dynamic refresh for a slot
   */
  public disableDynamicRefresh(slotId?: string): void {
    if (slotId) {
      this.dynamicRefreshSlots.delete(slotId);
      const slot = this.registeredSlots.get(slotId);
      if (slot?.refreshTimer) {
        clearInterval(slot.refreshTimer);
        slot.refreshTimer = undefined;
      }
      console.log(`📱 AdManager: Dynamic refresh disabled for ${slotId}`);
    } else {
      this.registeredSlots.forEach((slot, id) => {
        this.disableDynamicRefresh(id);
      });
    }
  }

  /**
   * Unregister an ad slot
   */
  public unregisterAdSlot(slotId: string): void {
    this.destroyAd(slotId);
  }

  /**
   * Add a direct deal for premium ad serving
   */
  public addDirectDeal(deal: any): void {
    const directDeal: DirectDeal = {
      dealId: deal.dealId,
      advertiserId: deal.advertiserId,
      campaignId: deal.campaignId,
      floorPrice: deal.floorPrice,
      priority: deal.priority,
      startDate: deal.startDate instanceof Date ? deal.startDate : new Date(deal.startDate),
      endDate: deal.endDate instanceof Date ? deal.endDate : new Date(deal.endDate),
      budget: deal.budget,
      spent: 0,
      impressionsServed: 0,
      impressionCap: deal.impressionCap,
      targeting: deal.targeting || {},
      status: 'active'
    };

    this.directDeals.push(directDeal);

    // If Prebid is available, configure the deal as a Prebid deal
    if (window.pbjs) {
      window.pbjs.que.push(() => {
        window.pbjs!.setConfig({
          deals: {
            enabled: true
          }
        });
      });
    }

    console.log(`📱 AdManager: Direct deal added: ${deal.dealId}`);
  }

  /**
   * Add a private marketplace deal
   */
  public addPrivateMarketplaceDeal(deal: any): void {
    const pmpDeal: PMPDeal = {
      dealId: deal.dealId,
      buyerId: deal.buyerId,
      sellerId: deal.sellerId,
      floorPrice: deal.floorPrice,
      priority: deal.priority,
      dealType: deal.dealType || 'preferred',
      budget: deal.budget,
      spent: 0,
      targeting: deal.targeting || {},
      status: 'active'
    };

    this.pmpDeals.push(pmpDeal);

    // Configure PMP deal in Prebid
    if (window.pbjs) {
      window.pbjs.que.push(() => {
        window.pbjs!.setConfig({
          deals: {
            enabled: true
          }
        });
      });
    }

    console.log(`📱 AdManager: PMP deal added: ${deal.dealId}`);
  }

  /**
   * Optimize floor prices based on historical performance
   */
  public async optimizeFloorPrices(): Promise<void> {
    console.log('📱 AdManager: Optimizing floor prices...');

    // Get current bid responses to analyze pricing
    if (window.pbjs) {
      const bidResponses = window.pbjs.getBidResponses();

      Object.entries(bidResponses).forEach(([adUnitCode, bids]: [string, any]) => {
        if (bids.bids && bids.bids.length > 0) {
          // Calculate optimal floor price as 70% of average winning CPM
          const avgCpm = bids.bids.reduce((sum: number, bid: any) => sum + (bid.cpm || 0), 0) / bids.bids.length;
          const optimalFloor = Math.round(avgCpm * 0.7 * 100) / 100;

          this.floorPrices[adUnitCode] = optimalFloor;
          console.log(`📱 AdManager: Floor price for ${adUnitCode}: $${optimalFloor} (avg CPM: $${avgCpm.toFixed(2)})`);
        }
      });

      // Apply floor prices via Prebid config
      window.pbjs.que.push(() => {
        window.pbjs!.setConfig({
          floors: {
            enforcement: {
              floorDeals: true,
              bidAdjustment: true
            },
            data: {
              currency: 'USD',
              schema: { fields: ['mediaType', 'size'] },
              values: Object.entries(this.floorPrices).reduce((acc, [slotId, floor]) => {
                acc[`banner|*`] = floor;
                return acc;
              }, {} as Record<string, number>)
            }
          }
        });
      });
    }

    console.log('📱 AdManager: Floor prices optimized');
  }

  /**
   * Get performance metrics
   */
  public getPerformanceMetrics(): AdMetrics {
    return { ...this.metricsCache };
  }

  /**
   * Update internal metrics cache
   */
  private updateMetrics(): void {
    if (this.impressionCount > 0) {
      this.metricsCache.fillRate = (this.impressionCount / Math.max(this.registeredSlots.size, 1)) * 100;
      this.metricsCache.ctr = this.clickCount > 0 ? (this.clickCount / this.impressionCount) * 100 : 0;
      this.metricsCache.rpm = this.impressionCount > 0 ? (this.metricsCache.revenue / this.impressionCount) * 1000 : 0;
    }
  }

  /**
   * Destroy all ads and clean up
   */
  public destroy(): void {
    console.log('📱 AdManager: Destroying all ads...');

    // Clear all refresh timers
    this.registeredSlots.forEach((slot, slotId) => {
      if (slot.refreshTimer) {
        clearInterval(slot.refreshTimer);
      }
    });

    // Destroy all GPT slots
    if (window.googletag) {
      const gptSlots = Array.from(this.registeredSlots.values())
        .filter(s => s.gptSlot)
        .map(s => s.gptSlot);

      if (gptSlots.length > 0) {
        window.googletag.cmd.push(() => {
          window.googletag!.destroySlots(gptSlots);
        });
      }
    }

    // Remove all Prebid ad units
    if (window.pbjs) {
      window.pbjs.que.push(() => {
        this.registeredSlots.forEach((_, slotId) => {
          try { window.pbjs!.removeAdUnit(slotId); } catch (e) { /* ignore */ }
        });
      });
    }

    this.registeredSlots.clear();
    this.directDeals = [];
    this.pmpDeals = [];
    this.floorPrices = {};
    this.dynamicRefreshSlots.clear();
    this.errorHandlers = [];
    this._isInitialized = false;
    this.prebidLoaded = false;
    this.gamLoaded = false;

    console.log('📱 AdManager: All ads destroyed');
  }
}

// Export singleton instance
const adManagerInstance = AdManager.getInstance();
export default adManagerInstance;
export { adManagerInstance as adManager };
